/** The Future Log: six months forward, composed from what the service has.
 *
 *  There is no future-log projection endpoint, so this is assembled here from
 *  three reads:
 *
 *  - dated entries across the whole span, in one range query (paged if a busy
 *    six months exceeds `MAX_PAGE_SIZE`);
 *  - calendar events across the whole span, one query, under the service's
 *    366-day range cap — and 503 when no provider is configured, which is a
 *    state rather than a failure;
 *  - each month's task inbox, which a date range cannot return because those
 *    tasks carry `active_month` and no date.
 *
 *  That is one call per month plus two, for one screen. A
 *  `/v1/future-log?months=6` would make it one; until then this is the honest
 *  composition rather than a screen that pretends to have less on it.
 *
 *  **It looks forward, and that is the whole selection rule.** Only days *after*
 *  today appear, plus each month's undated inbox. Today and everything behind it
 *  belong to the Daily Log, and including them turned the current month into a
 *  replay of the rapid log — twenty-two rows all reading `02` where the frame
 *  shows five. The frame does not state the rule; this is the reading that makes
 *  the screen answer the question it is for, which is what is coming.
 */

import { request, type Session } from './client';
import { calendarWindowEnd } from './adapter';
import { monthFrom, monthLabel, shiftMonth, type MonthRange } from './month';
import type { WireCalendarEvent, WireEntry, WireEventRange } from './wire';

/** How far forward the log looks. The frame is six months, current month first. */
export const FUTURE_MONTHS = 6;

/** How many of those months show their days rather than a count. */
export const EXPANDED_MONTHS = 2;

export interface FutureItem {
  /** `02`, `17–19`, or `—` for a month-inbox task that has no date. */
  when: string;
  /** Drives the signifier, so state is never carried by colour alone. */
  state: 'event' | 'open' | 'done' | 'migrated' | 'cancelled' | 'note' | 'question';
  body: string;
  /** Sorted on; undated inbox items sort last within their month. */
  at: string;
}

export interface FutureMonth {
  /** `2026-08`. */
  month: string;
  /** `AUG 2026`, as the frame heads each month. */
  label: string;
  days: string[];
  /** Days with something on them. Rendered brighter — never colour alone, so
   *  the summary list below stays the real content. */
  marked: Set<string>;
  items: FutureItem[];
}

/** `AUGUST 2026` → `AUG 2026`. */
export function shortMonthLabel(month: string): string {
  const [name, year] = monthLabel(month).split(' ');
  return `${(name ?? '').slice(0, 3)} ${year ?? ''}`;
}

/** The span the log covers, current month first. */
export function futureRange(journalDate: string, count = FUTURE_MONTHS): MonthRange[] {
  const months: MonthRange[] = [];
  let month = journalDate.slice(0, 7);
  for (let i = 0; i < count; i += 1) {
    months.push(monthFrom(month));
    month = shiftMonth(month, 1);
  }
  return months;
}

const DAY = (isoDate: string) => isoDate.slice(8, 10);

/** A day, or `DD–DD` for something spanning several. An en dash, matching the
 *  frame and the rest of the product's ranges. */
function span(from: string, to: string): string {
  return from === to ? DAY(from) : `${DAY(from)}–${DAY(to)}`;
}

/** An all-day event's `end` is exclusive, which is a classic off-by-one: a
 *  single-day event on the 2nd ends on the 3rd, and reading that literally
 *  reports `02–03`. */
function lastDay(event: WireCalendarEvent): string {
  const end = (event.end ?? '').slice(0, 10);
  const start = (event.start ?? '').slice(0, 10);
  if (!event.all_day || !end || end <= start) return end || start;
  const at = new Date(`${end}T00:00:00Z`);
  at.setUTCDate(at.getUTCDate() - 1);
  const previous = at.toISOString().slice(0, 10);
  return previous < start ? start : previous;
}

function entryState(entry: WireEntry): FutureItem['state'] {
  if (entry.kind === 'note') return 'note';
  if (entry.kind === 'question') return entry.state === 'answered' ? 'done' : 'question';
  if (entry.state === 'done' || entry.state === 'migrated' || entry.state === 'cancelled') {
    return entry.state;
  }
  return 'open';
}

async function datedEntries(session: Session, from: string, to: string): Promise<WireEntry[]> {
  const found: WireEntry[] = [];
  let after: string | null = null;
  // Six busy months can exceed one page, and a silently truncated future log is
  // worse than a slow one.
  for (let page = 0; page < 12; page += 1) {
    const query: string = `from=${from}&to=${to}&limit=100${after ? `&after=${after}` : ''}`;
    const body: { items: WireEntry[]; next_cursor: string | null } = (
      await request<{ items: WireEntry[]; next_cursor: string | null }>(
        session,
        `/v1/entries?${query}`,
      )
    ).body;
    found.push(...body.items);
    if (!body.next_cursor) return found;
    after = body.next_cursor;
  }
  return found;
}

export async function futureLog(
  session: Session,
  journalDate: string,
  count = FUTURE_MONTHS,
): Promise<FutureMonth[]> {
  const months = futureRange(journalDate, count);
  const from = months[0]?.from ?? journalDate;
  const to = months[months.length - 1]?.to ?? journalDate;

  const [entries, events, inboxes] = await Promise.all([
    datedEntries(session, from, to),
    request<WireEventRange>(session, `/v1/calendar/events?from=${from}&to=${calendarWindowEnd(to)}`)
      .then((r) => r.body.events ?? [])
      // No provider configured answers 503. The log still has entries and
      // inboxes to show, so it degrades rather than failing.
      .catch(() => [] as WireCalendarEvent[]),
    Promise.all(
      months.map((month) =>
        request<{ items: WireEntry[] }>(
          session,
          `/v1/entries?kind=task&active_month=${month.month}&limit=100`,
        )
          .then((r) => r.body.items)
          .catch(() => [] as WireEntry[]),
      ),
    ),
  ]);

  return months.map((month, index) => {
    const items: FutureItem[] = [];
    const marked = new Set<string>();

    for (const event of events) {
      const start = (event.start ?? '').slice(0, 10);
      if (start.slice(0, 7) !== month.month) continue;
      if (start <= journalDate) continue;
      const end = lastDay(event);
      items.push({ when: span(start, end), state: 'event', body: event.title ?? '', at: start });
      for (const day of month.days) {
        if (day >= start && day <= end) marked.add(day);
      }
    }

    for (const entry of entries) {
      const day = entry.active_date ?? '';
      if (day.slice(0, 7) !== month.month) continue;
      if (day <= journalDate) continue;
      items.push({
        when: DAY(day),
        state: entryState(entry),
        body: entry.content,
        at: day,
      });
      marked.add(day);
    }

    items.sort((a, b) => a.at.localeCompare(b.at) || a.body.localeCompare(b.body));

    // The inbox has no dates, so it cannot mark a day and it sorts last — the
    // frame shows these with `—` where a day number would be.
    for (const task of inboxes[index] ?? []) {
      items.push({ when: '—', state: entryState(task), body: task.content, at: '' });
    }

    return { month: month.month, label: shortMonthLabel(month.month), days: month.days, marked, items };
  });
}
